const translations = {
  en: {
    nav: {
      home: "Home",
      contact: "Contact",
    },
    hero: {
      badge: "Professional Solar Panel Cleaning",
      title: "Dirty Panels Are Costing You Money",
      subtitle:
        "Your solar panels work hard every single day — but dust, bird droppings, and grime silently steal up to 25% of their energy output. PowerShine gives back what's yours.",
      cta: "Get a Free Quote",
      secondary: "Our Services",
    },
    stats: [
      { value: "25%", label: "Energy lost to dirty panels" },
      { value: "100%", label: "Pure water system — zero residue" },
    ],
    problem: {
      title: "The Problem Most People Don't Know They Have",
      text: "The Netherlands is one of Europe's solar energy leaders — rooftops, warehouses, and farms covered in panels. But here's what nobody tells you: most people have no idea their panels need regular cleaning. That invisible layer of dust and grime isn't just dirt. It's lost electricity. It's money you've already paid for but never received. We don't just clean panels. We sell awareness — and then we back it up with results.",
    },
    services: {
      title: "What We Do",
      subtitle: "Specialized, not scattered. We do one thing — and we do it better than anyone.",
      items: [
        {
          icon: "☀️",
          title: "Solar Panel Cleaning",
          desc: "Pure-water fed pole system with solar-safe brushes. No scratches, no residue, maximum light absorption restored. TDS-certified water purity on every job.",
        },
        {
          icon: "🏭",
          title: "Corporate Maintenance Contracts",
          desc: "Monthly or quarterly plans for warehouses, agricultural businesses, and companies with large panel installations. This is where the real efficiency gains live.",
        },
        {
          icon: "📈",
          title: "Performance Assessment",
          desc: "Not sure how much output you're losing? We assess your installation and show you the exact numbers — before and after. Data you can act on.",
        },
      ],
    },
    why: {
      title: "Why PowerShine?",
      subtitle: "Specialization builds trust. Trust commands better results — and better rates.",
      items: [
        {
          icon: "💧",
          title: "Pure Water System",
          desc: "We use a water-fed pole system with purified water. No soap, no chemicals, no streaks — just pure performance.",
        },
        {
          icon: "🔬",
          title: "TDS Water Testing",
          desc: "Every job starts with a TDS measurement to guarantee water purity. Your panels get only the cleanest water.",
        },
        {
          icon: "🛡️",
          title: "Panel-Safe Equipment",
          desc: "Specialized brushes designed specifically for solar panel surfaces. No scratching, no damage, ever.",
        },
        {
          icon: "📋",
          title: "Certified & Insured",
          desc: "Professional safety equipment on every job. We take no shortcuts — with your property or our team.",
        },
      ],
    },
    targets: {
      title: "Who We Work With",
      items: [
        { icon: "🏗️", label: "Warehouses & Industrial Sites" },
        { icon: "🌾", label: "Agricultural Businesses & Farms" },
        { icon: "🏠", label: "Modern Homes with Solar Panels" },
        { icon: "🏢", label: "Commercial Buildings" },
      ],
      pitch:
        "We tell businesses the truth: dirty panels mean lost electricity production. Lost production means lost money. That's a conversation that leads to signed contracts.",
    },
    cta: {
      title: "Ready to Recover Your Lost Energy?",
      subtitle:
        "Get a no-obligation quote today. We'll assess your panels and tell you exactly what you're losing — and exactly how we'll fix it.",
      button: "Request a Free Quote",
    },
    contact: {
      title: "Contact Us",
      subtitle: "Ready to get your panels performing at 100%? Let's talk.",
      form: {
        name: "Full Name",
        email: "Email Address",
        phone: "Phone Number",
        service: "Service Interested In",
        serviceOptions: [
          "Select a service",
          "Solar Panel Cleaning",
          "Corporate Contract",
          "Performance Assessment",
          "Other",
        ],
        message: "Your Message",
        messagePlaceholder:
          "Tell us about your property, number of panels, or any questions you have...",
        submit: "Send Message",
        sending: "Sending...",
      },
      info: {
        title: "Get in Touch",
        location: { label: "Location", value: "The Netherlands" },
        email: { label: "Email", value: "info@powershine.nl" },
        phone: { label: "Phone", value: "+31 6 11366797" },
        hours: { label: "Working Hours", value: "Mon–Fri: 08:00–18:00" },
      },
      success: "Thank you! Your message has been sent. We'll get back to you within 24 hours.",
    },
    footer: {
      tagline: "Professional solar panel cleaning in the Netherlands.",
      links: { home: "Home", contact: "Contact" },
      copy: "© 2026 PowerShine. All rights reserved.",
      builtBy: "Website by",
    },
  },

  nl: {
    nav: {
      home: "Home",
      contact: "Contact",
    },
    hero: {
      badge: "Professionele Zonnepanelen Reiniging",
      title: "Vuile Panelen Kosten U Geld",
      subtitle:
        "Uw zonnepanelen werken elke dag hard — maar stof, vogelpoep en vuil stelen stil tot 25% van uw energieopbrengst. PowerShine geeft u terug wat van u is.",
      cta: "Gratis Offerte Aanvragen",
      secondary: "Onze Diensten",
    },
    stats: [
      { value: "25%", label: "Energieverlies door vuile panelen" },
      { value: "100%", label: "Puur water systeem — geen resten" },
    ],
    problem: {
      title: "Het Probleem Dat De Meeste Mensen Niet Kennen",
      text: "Nederland is één van Europa's koplopers op het gebied van zonne-energie — daken, loodsen en boerderijen vol met panelen. Maar wat niemand u vertelt: de meeste mensen weten niet dat hun panelen regelmatig gereinigd moeten worden. Die onzichtbare laag stof en vuil is niet zomaar viezigheid. Het is verloren elektriciteit. Het is geld dat u al heeft betaald maar nooit heeft ontvangen. Wij verkopen niet alleen een schoonmaakdienst. Wij verkopen bewustzijn — en leveren daarna het bewijs.",
    },
    services: {
      title: "Wat Wij Doen",
      subtitle:
        "Gespecialiseerd, niet algemeen. Wij doen één ding — en we doen het beter dan wie dan ook.",
      items: [
        {
          icon: "☀️",
          title: "Zonnepanelen Reinigen",
          desc: "Puur-water systeem met zonnepaneel-veilige borstels. Geen krassen, geen resten, maximale lichtabsorptie hersteld. TDS-gecertificeerde waterkwaliteit bij elke klus.",
        },
        {
          icon: "🏭",
          title: "Onderhoudscontracten voor Bedrijven",
          desc: "Maandelijkse of kwartaalplannen voor loodsen, agrarische bedrijven en organisaties met grote zonnepaneelinstallaties. Hier liggen de echte efficiëntiewinsten.",
        },
        {
          icon: "📈",
          title: "Prestatiemeting",
          desc: "Weet u niet hoeveel opbrengst u misloopt? Wij beoordelen uw installatie en tonen u de exacte cijfers — voor én na de reiniging. Data waar u op kunt handelen.",
        },
      ],
    },
    why: {
      title: "Waarom PowerShine?",
      subtitle:
        "Specialisatie schept vertrouwen. Vertrouwen leidt tot betere resultaten — en betere tarieven.",
      items: [
        {
          icon: "💧",
          title: "Puur Water Systeem",
          desc: "Wij gebruiken een water-fed pole systeem met gezuiverd water. Geen zeep, geen chemicaliën, geen strepen — alleen pure prestaties.",
        },
        {
          icon: "🔬",
          title: "TDS Watertest",
          desc: "Elke klus begint met een TDS-meting om de waterkwaliteit te garanderen. Uw panelen krijgen alleen het schoonste water.",
        },
        {
          icon: "🛡️",
          title: "Paneelveilige Apparatuur",
          desc: "Gespecialiseerde borstels ontworpen voor zonnepanelen. Geen krassen, geen beschadiging, nooit.",
        },
        {
          icon: "📋",
          title: "Gecertificeerd & Verzekerd",
          desc: "Professionele veiligheidsuitrusting bij elke klus. Geen compromissen — niet met uw eigendom, niet met ons team.",
        },
      ],
    },
    targets: {
      title: "Met Wie Wij Werken",
      items: [
        { icon: "🏗️", label: "Loodsen & Industriële Locaties" },
        { icon: "🌾", label: "Agrarische Bedrijven & Boerderijen" },
        { icon: "🏠", label: "Moderne Woningen met Zonnepanelen" },
        { icon: "🏢", label: "Commerciële Gebouwen" },
      ],
      pitch:
        "We vertellen bedrijven de waarheid: vuile panelen betekenen minder elektriciteitsproductie. Minder productie betekent minder geld. Dat is een gesprek dat leidt tot ondertekende contracten.",
    },
    cta: {
      title: "Klaar Om Uw Verloren Energie Terug Te Krijgen?",
      subtitle:
        "Vraag vandaag nog een vrijblijvende offerte aan. Wij beoordelen uw panelen en vertellen u precies wat u verliest — en precies hoe wij dat oplossen.",
      button: "Gratis Offerte Aanvragen",
    },
    contact: {
      title: "Neem Contact Op",
      subtitle:
        "Klaar om uw panelen op 100% te laten presteren? Laten we praten.",
      form: {
        name: "Volledige Naam",
        email: "E-mailadres",
        phone: "Telefoonnummer",
        service: "Dienst Waarin U Geïnteresseerd Bent",
        serviceOptions: [
          "Selecteer een dienst",
          "Zonnepanelen Reinigen",
          "Bedrijfscontract",
          "Prestatiemeting",
          "Anders",
        ],
        message: "Uw Bericht",
        messagePlaceholder:
          "Vertel ons over uw pand, aantal panelen, of stel gerust uw vragen...",
        submit: "Bericht Versturen",
        sending: "Versturen...",
      },
      info: {
        title: "Contactgegevens",
        location: { label: "Locatie", value: "Nederland" },
        email: { label: "E-mail", value: "info@powershine.nl" },
        phone: { label: "Telefoon", value: "+31 6 11366797" },
        hours: { label: "Openingstijden", value: "Ma–Vr: 08:00–18:00" },
      },
      success:
        "Bedankt! Uw bericht is verzonden. We nemen binnen 24 uur contact met u op.",
    },
    footer: {
      tagline:
        "Professionele reiniging van zonnepanelen in Nederland.",
      links: { home: "Home", contact: "Contact" },
      copy: "© 2026 PowerShine. Alle rechten voorbehouden.",
      builtBy: "Website door",
    },
  },
};

export default translations;
